import { LatLngBounds } from 'leaflet'
import Vue from 'vue'
import Vuex, { Store } from 'vuex'
import { Router, QueryParameters } from './router'

Vue.use(Vuex)
const POLAND_COORD = [[54.4732297, 29.9542236], [49.5501617, 12.3760986]]
const KRAKOW_COORD = [[50.1261338, 20.2173455], [49.9676668, 19.7922355]]
const DEFAULT_MAP_BOUNDS_ARRAY = POLAND_COORD
const FETCH_PLACES_REQUEST_URL_BASIS = `${location.origin}/watchout/api/places`
const FETCH_TAGS_REQUEST_URL_BASIS = `${location.origin}/watchout/api/tags`
const NORTHWEST_MAP_CORNER_COORDINATES_PARAMETER_KEY = 'ne'
const SOUTHEAST_MAP_CORNER_COORDINATES_PARAMETER_KEY = 'sw'
const LIKED_PLACES_PARAMETER_KEY = 'lp'
const EVENT_IDENTIFIER_PARAMETER_KEY = 'event'
const TAG_IDENTIFIER_PARAMETER_KEY = 'tag'
const SEARCH_INPUT_BOXES_AUTOMATIC_HIDING_STATUS_LOCALE_STORAGE_KEY = 'searchInputBoxesShouldNotHideAutomatically'
const CURRENT_LANGUAGE_LOCALE_STORAGE_KEY = 'currentLanguage'
const LIKED_PLACES_STORAGE_KEY = 'likedPlaces'
const LAST_COORDINATES_LOCALE_STORAGE_KEY = 'lastCoordinates'

function queryFromWindowLocation () : Record<string, string> {
  const params = new URLSearchParams(window.location.search || '')
  const query: Record<string, string> = {}
  params.forEach((value, key) => {
    // Keep first value; comma-separated tags stay as one string
    if (!(key in query)) {
      query[key] = value
    }
  })
  return query
}

function parseScheduleTagIdsFromQuery (query: Record<string, any>) : number[] {
  const raw = query[QueryParameters.ScheduleTags]
  if (raw == null || raw === '') {
    return []
  }
  const parts = Array.isArray(raw) ? raw : String(raw).split(',')
  return parts
    .map((part: string) => parseInt(String(part).trim(), 10))
    .filter((id: number) => !isNaN(id))
}

function scheduleFiltersFromQuery (query: Record<string, any>) {
  const radiusRaw = parseInt(String(query[QueryParameters.ScheduleRadiusKm] || ''), 10)
  const showAllRaw = String(query[QueryParameters.ScheduleShowAll] || '').toLowerCase()
  return {
    tagIds: parseScheduleTagIdsFromQuery(query),
    datetimeFrom: (query[QueryParameters.ScheduleDatetimeFrom] as string) || '',
    datetimeTo: (query[QueryParameters.ScheduleDatetimeTo] as string) || '',
    radiusKm: (!isNaN(radiusRaw) && radiusRaw >= 1 && radiusRaw <= 10) ? radiusRaw : null as null | number,
    showAllPlaces: showAllRaw === '1' || showAllRaw === 'true'
  }
}

function syncScheduleFiltersToRoute (state: any) {
  if (!state.isSchedule) {
    return
  }
  const tagIds = (state.selectedTags || [])
    .map((tag: any) => tag && tag.id)
    .filter((id: any) => id != null)
  const next: Record<string, string | undefined> = {
    [QueryParameters.ScheduleTags]: tagIds.length ? tagIds.join(',') : undefined,
    [QueryParameters.ScheduleDatetimeFrom]: state.scheduleDatetimeFrom || undefined,
    [QueryParameters.ScheduleDatetimeTo]: state.scheduleDatetimeTo || undefined,
    [QueryParameters.ScheduleRadiusKm]: state.scheduleShowAllPlaces ? undefined : String(state.scheduleRadiusKm),
    [QueryParameters.ScheduleShowAll]: state.scheduleShowAllPlaces ? '1' : undefined
  }
  state.router.setQueryParameters(next, false)
}

function resolveSiteProduct (): string {
  const fromWindow = (window as any).__SITE_PRODUCT__
  if (typeof fromWindow === 'string' && fromWindow) {
    return fromWindow
  }
  return 'pubwatch'
}

function resolveSiteProductDisplayName (siteProduct: string): string {
  const fromWindow = (window as any).__SITE_PRODUCT_DISPLAY_NAME__
  if (typeof fromWindow === 'string' && fromWindow.trim()) {
    return fromWindow.trim()
  }
  if (siteProduct === 'lunchmap') return 'LunchOut'
  if (siteProduct === 'alleventschedule') return 'allEventSchedule'
  if (siteProduct === 'plants4pets') return 'Plants4Pets'
  return 'PubWatch'
}

function fetchJSON (requestURL: URL) {
  return fetch(requestURL.toString()).then(response => {
    if (response.ok) {
      return response.json()
    } else {
      throw new Error(response.status.toString())
    }
  })
}

export default (router: Router) => {
  // router = Vue.observable(router)
  const siteProduct = resolveSiteProduct()
  const siteProductDisplayName = resolveSiteProductDisplayName(siteProduct)
  const isSchedule = siteProduct === 'alleventschedule'
  // Prefer window.location — router.currentRoute.query is often empty before app mount
  const fromQuery = scheduleFiltersFromQuery({
    ...queryFromWindowLocation(),
    ...(router.currentRoute.query || {})
  })

  return new Store({
    state: {
      router,
      selectedLocation: undefined as any,
      selectedEvent: undefined as any,
      selectedTag: undefined as any,
      selectedTags: [] as any[],
      fetchedPlaces: undefined as undefined | any[],
      searchInputBoxesHiddenWhenScreenIsSmallEnough: undefined as undefined | boolean,
      searchInputBoxesShouldNotHideAutomatically: localStorage?.getItem(SEARCH_INPUT_BOXES_AUTOMATIC_HIDING_STATUS_LOCALE_STORAGE_KEY)
        && (localStorage.getItem(SEARCH_INPUT_BOXES_AUTOMATIC_HIDING_STATUS_LOCALE_STORAGE_KEY) === 'true') as undefined | boolean,
      currentLanguage: localStorage?.getItem(CURRENT_LANGUAGE_LOCALE_STORAGE_KEY) || 'en' as string,
      siteProduct,
      siteProductDisplayName,
      isLunchMap: siteProduct === 'lunchmap',
      isSchedule,
      likedPlaces: JSON.parse(localStorage?.getItem(LIKED_PLACES_STORAGE_KEY) || '[]') || [] as number[],
      currentLunchPlaceDisplay: undefined as any,
      banners: {} as Record<string, any>,
      scheduleRadiusKm: fromQuery.radiusKm != null ? fromQuery.radiusKm : 5,
      scheduleShowAllPlaces: !!fromQuery.showAllPlaces,
      scheduleDatetimeFrom: fromQuery.datetimeFrom,
      scheduleDatetimeTo: fromQuery.datetimeTo,
      scheduleTagIdsFromRoute: fromQuery.tagIds as number[],
      scheduleListVisible: false
    },
    getters: {
      mapBounds (state) : LatLngBounds {
        return new LatLngBounds((state.router.currentRoute.query[QueryParameters.MapBounds]
          && JSON.parse(state.router.currentRoute.query[QueryParameters.MapBounds] as string)) ||
          (localStorage?.getItem(LAST_COORDINATES_LOCALE_STORAGE_KEY) && JSON.parse(localStorage.getItem(LAST_COORDINATES_LOCALE_STORAGE_KEY) || '')) ||
           DEFAULT_MAP_BOUNDS_ARRAY)
      }
    },
    mutations: {
      setSelectedLocation (state, selectedLocation) {
        state.selectedLocation = selectedLocation
      },
      setSelectedEvent (state, { selectedEvent, addHistoryEntry }) {
        if (!selectedEvent) {
          state.router.currentRoute.query[QueryParameters.SelectedEventIdentifier] && state.router.removeQueryParameter(QueryParameters.SelectedEventIdentifier, addHistoryEntry)
        } else if (selectedEvent['id'] != (state.router.currentRoute.query[QueryParameters.SelectedEventIdentifier] as string)) {
          state.router.setQueryParameter(QueryParameters.SelectedEventIdentifier, selectedEvent['id'], addHistoryEntry)
        }
        state.selectedEvent = selectedEvent
      },
      setSelectedTag (state, { selectedTag, addHistoryEntry }) {
        if (!selectedTag) {
          state.router.currentRoute.query[QueryParameters.SelectedTagIdentifier] && state.router.removeQueryParameter(QueryParameters.SelectedTagIdentifier, addHistoryEntry)
        } else if (selectedTag['id'] != (state.router.currentRoute.query[QueryParameters.SelectedTagIdentifier] as string)) {
          state.router.setQueryParameter(QueryParameters.SelectedTagIdentifier, selectedTag['id'], addHistoryEntry)
        }
        state.selectedTag = selectedTag
      },
      setSelectedTags (state, selectedTags: any[]) {
        state.selectedTags = Array.isArray(selectedTags) ? selectedTags : []
        syncScheduleFiltersToRoute(state)
      },
      applyScheduleFiltersFromParsed (state, parsed: { datetimeFrom?: string, datetimeTo?: string, radiusKm?: number | null, tagIds?: number[], showAllPlaces?: boolean }) {
        if (parsed.datetimeFrom) {
          state.scheduleDatetimeFrom = parsed.datetimeFrom
        }
        if (parsed.datetimeTo) {
          state.scheduleDatetimeTo = parsed.datetimeTo
        }
        if (parsed.radiusKm != null) {
          state.scheduleRadiusKm = parsed.radiusKm
        }
        if (parsed.showAllPlaces != null) {
          state.scheduleShowAllPlaces = parsed.showAllPlaces
        }
        if (parsed.tagIds) {
          state.scheduleTagIdsFromRoute = parsed.tagIds
        }
      },
      setMapBounds (state, { mapBounds, addHistoryEntry }) {
        localStorage.setItem(LAST_COORDINATES_LOCALE_STORAGE_KEY, JSON.stringify([[mapBounds.getNorth(), mapBounds.getEast()], [mapBounds.getSouth(), mapBounds.getWest()]]))
        state.router.setQueryParameter(QueryParameters.MapBounds, JSON.stringify([[mapBounds.getNorth(), mapBounds.getEast()], [mapBounds.getSouth(), mapBounds.getWest()]]), false)
      },
      setFetchedPlaces (state, fetchedPlaces) {
        state.fetchedPlaces = fetchedPlaces
      },
      setCurrentLanguage (state, newCurrentLanguage) {
        state.currentLanguage = newCurrentLanguage
        localStorage?.setItem(CURRENT_LANGUAGE_LOCALE_STORAGE_KEY, newCurrentLanguage)
      },
      setLikedPlaces(state, likedPlaces) {
        state.likedPlaces = likedPlaces
        localStorage?.setItem(LIKED_PLACES_STORAGE_KEY, JSON.stringify(state.likedPlaces))
      },
      setSearchInputBoxesVisibilityStatusOnSmallEnoughScreens (state, searchInputBoxesHiddenWhenScreenIsSmallEnough: boolean) {
        state.searchInputBoxesHiddenWhenScreenIsSmallEnough = searchInputBoxesHiddenWhenScreenIsSmallEnough
      },
      setIfSearchInputBoxesShouldNotHideAutomatically (state, searchInputBoxesShouldNotHideAutomatically: boolean) {
        state.searchInputBoxesShouldNotHideAutomatically = searchInputBoxesShouldNotHideAutomatically
        localStorage?.setItem(SEARCH_INPUT_BOXES_AUTOMATIC_HIDING_STATUS_LOCALE_STORAGE_KEY, searchInputBoxesShouldNotHideAutomatically.toString())
      },
      setBanners (state, banners: Record<string, any>) {
        state.banners = banners || {}
      },
      setScheduleRadiusKm (state, km: number) {
        state.scheduleRadiusKm = Math.max(1, Math.min(10, Number(km) || 5))
        syncScheduleFiltersToRoute(state)
      },
      setScheduleShowAllPlaces (state, value: boolean) {
        state.scheduleShowAllPlaces = !!value
        syncScheduleFiltersToRoute(state)
      },
      setScheduleDatetimeFrom (state, value: string) {
        state.scheduleDatetimeFrom = value || ''
        syncScheduleFiltersToRoute(state)
      },
      setScheduleDatetimeTo (state, value: string) {
        state.scheduleDatetimeTo = value || ''
        syncScheduleFiltersToRoute(state)
      },
      setScheduleListVisible (state, visible: boolean) {
        state.scheduleListVisible = !!visible
      },
      setCurrentLunchPlaceDisplay (state, newCurrentLunchPlaceDisplay) {
        if(state.currentLunchPlaceDisplay && state.currentLunchPlaceDisplay.placelunchpost_set){
          const style = document.getElementById('current_lunch_place_display-' + state.currentLunchPlaceDisplay.id);
          if (style) {
            document.head.removeChild(style);
          }
        }

        state.currentLunchPlaceDisplay = newCurrentLunchPlaceDisplay;

        if(state.currentLunchPlaceDisplay && state.currentLunchPlaceDisplay.placelunchpost_set){
          const style = document.createElement('style');
          style.type = 'text/css';
          style.id = 'current_lunch_place_display-' + state.currentLunchPlaceDisplay.id

          const placelunchposts = state.currentLunchPlaceDisplay.placelunchpost_set;
          var css = '';
          for (let i in placelunchposts) {
            css += "\n" + placelunchposts[i].custom_styles;
          }
          if(css){
            style.appendChild(document.createTextNode(css));
            document.head.appendChild(style);
          }
        }
      }
    },
    actions: {
      hydrateScheduleTagsFromRoute ({ state, commit }) {
        if (!state.isSchedule) {
          return Promise.resolve()
        }
        // Re-read URL in case store init ran before the query was available
        const parsed = scheduleFiltersFromQuery({
          ...queryFromWindowLocation(),
          ...(state.router.currentRoute.query || {})
        })
        commit('applyScheduleFiltersFromParsed', parsed)

        const ids: number[] = parsed.tagIds || state.scheduleTagIdsFromRoute || []
        if (!ids.length) {
          return Promise.resolve()
        }
        return fetchJSON(new URL(FETCH_TAGS_REQUEST_URL_BASIS)).then(data => {
          const results = (data && data.results) || []
          const idSet = new Set(ids)
          const selected = results.filter((tag: any) => idSet.has(tag.id))
          selected.sort((a: any, b: any) => ids.indexOf(a.id) - ids.indexOf(b.id))
          commit('setSelectedTags', selected)
        }).catch(() => {
          commit('setSelectedTags', ids.map(id => ({ id, name: String(id) })))
        })
      },
      toggleLikedPlace({state, commit}, placeId) {
        let likedPlaces = state.likedPlaces;
        if(likedPlaces.includes(placeId)) {
          likedPlaces = likedPlaces.filter((value: number, index: number, array: number[]) => value !== placeId);
        } else {
        likedPlaces.push(placeId)
        }
        const filteredLikedPlaces = likedPlaces.filter((value: number, index: number, array: number[]) => array.indexOf(value) === index);
        commit('setLikedPlaces', filteredLikedPlaces)
      },
      fetchPlaces ({ state, getters, commit }, triggeredByMapBoundsChange: boolean) {
        const placesFetchRequestURL = new URL(FETCH_PLACES_REQUEST_URL_BASIS)
        if (state.isSchedule) {
          if (!state.scheduleShowAllPlaces) {
            const centerLat = getters.mapBounds.getCenter().lat
            const centerLng = getters.mapBounds.getCenter().lng
            placesFetchRequestURL.searchParams.append('center', String(centerLat))
            placesFetchRequestURL.searchParams.append('center', String(centerLng))
            placesFetchRequestURL.searchParams.set('radius_km', String(state.scheduleRadiusKm))
          }
          if (state.scheduleDatetimeFrom) {
            placesFetchRequestURL.searchParams.set('datetime_from', state.scheduleDatetimeFrom)
          }
          if (state.scheduleDatetimeTo) {
            placesFetchRequestURL.searchParams.set('datetime_to', state.scheduleDatetimeTo)
          }
          ;(state.selectedTags || []).forEach((tag: any) => {
            if (tag && tag.id != null) {
              placesFetchRequestURL.searchParams.append('tags', String(tag.id))
            }
          })
        } else {
          placesFetchRequestURL.searchParams.set(NORTHWEST_MAP_CORNER_COORDINATES_PARAMETER_KEY, getters.mapBounds.getNorth().toString())
          placesFetchRequestURL.searchParams.append(NORTHWEST_MAP_CORNER_COORDINATES_PARAMETER_KEY, getters.mapBounds.getWest().toString())
          placesFetchRequestURL.searchParams.set(SOUTHEAST_MAP_CORNER_COORDINATES_PARAMETER_KEY, getters.mapBounds.getSouth().toString())
          placesFetchRequestURL.searchParams.append(SOUTHEAST_MAP_CORNER_COORDINATES_PARAMETER_KEY, getters.mapBounds.getEast().toString())
          placesFetchRequestURL.searchParams.set(LIKED_PLACES_PARAMETER_KEY, state.likedPlaces)
          state.selectedEvent && placesFetchRequestURL.searchParams.set(EVENT_IDENTIFIER_PARAMETER_KEY, state.selectedEvent['id'])
          state.selectedTag && placesFetchRequestURL.searchParams.set(TAG_IDENTIFIER_PARAMETER_KEY, state.selectedTag['id'])
        }
        return fetchJSON(placesFetchRequestURL).then(data => {
          if (triggeredByMapBoundsChange && !state.isSchedule) {
            if (data['count']) {
              if (state.fetchedPlaces && state.fetchedPlaces.length) {
                const placesIdentifiers = new Set(state.fetchedPlaces.map((place: any) => place['id']))
                commit('setFetchedPlaces', state.fetchedPlaces.concat(data['results'].filter((place: any) => !placesIdentifiers.has(place['id']))))
              } else {
                commit('setFetchedPlaces', data['results'])
              }
            }
          } else {
            commit('setFetchedPlaces', data['results'])
          }
          return data['results']
        })
      },
      fetchPlace ({ state, commit }, placeIdentifier) {
        const placeFetchRequestURL = new URL(placeIdentifier, FETCH_PLACES_REQUEST_URL_BASIS + '/')
        state.selectedEvent && placeFetchRequestURL.searchParams.set(EVENT_IDENTIFIER_PARAMETER_KEY, state.selectedEvent['id'])
        state.selectedTag && placeFetchRequestURL.searchParams.set(TAG_IDENTIFIER_PARAMETER_KEY, state.selectedTag['id'])
        return fetchJSON(placeFetchRequestURL).then(place => {
          commit('setFetchedPlaces', (state.fetchedPlaces?.push(place) && state.fetchedPlaces) || [place])
          return place
        })
      }
    }
  })
}
